import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import StepIndicator from "@/components/wizard/StepIndicator";
import Step1PersonalDetails from "@/components/wizard/Step1PersonalDetails";
import Step2AddressHouse from "@/components/wizard/Step2AddressHouse";
import Step3Items from "@/components/wizard/Step3Items";
import Step4DateTime from "@/components/wizard/Step4DateTime";
import Step5Summary from "@/components/wizard/Step5Summary";
import StepRoomPhotos from "@/components/wizard/StepRoomPhotos";
import { Button } from "@/components/ui/button";
import { createBooking } from "@/lib/api";
import { isValidUKPhone, isValidUKPostcode, normalizePostcode } from "@/lib/validation";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TOTAL_STEPS = 6;

const INITIAL = {
  full_name: "",
  phone: "",
  email: "",
  street_address: "",
  postcode: "",
  property_type: "",
  room_photos: [],
  items: [],
  preferred_date: "",
  access_notes: "",
};

export default function BookingWizard() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const canNext = useMemo(() => {
    if (step === 1) {
      return (
        data.street_address.trim().length > 2 &&
        isValidUKPostcode(data.postcode) &&
        !!data.property_type
      );
    }
    if (step === 2) return data.items.length >= 1;
    if (step === 3) return data.room_photos.length >= 1;
    if (step === 4) return !!data.preferred_date;
    if (step === 5) {
      return (
        data.full_name.trim().length > 1 &&
        isValidUKPhone(data.phone) &&
        EMAIL_RE.test(data.email)
      );
    }
    return true;
  }, [step, data]);

  function next() {
    if (!canNext) {
      toast.error(missingMessage(step, data));
      return;
    }
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function back() {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function submit() {
    setSubmitting(true);
    try {
      const payload = {
        full_name: data.full_name.trim(),
        phone: data.phone.trim(),
        email: data.email.trim(),
        street_address: data.street_address.trim(),
        postcode: normalizePostcode(data.postcode),
        property_type: data.property_type,
        room_photo_ids: data.room_photos.map((p) => p.id),
        items: data.items.map((it) => ({
          name: it.name,
          quantity: it.quantity,
        })),
        preferred_date: data.preferred_date,
        access_notes: data.access_notes,
      };
      const res = await createBooking(payload);
      toast.success(`Booking submitted (${res.reference})`);
      navigate("/booking/success", { state: { reference: res.reference, email: data.email } });
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Failed to submit booking");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="mb-8">
          <StepIndicator current={step} />
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 shadow-xl p-6 sm:p-10" data-testid="wizard-panel">
          {step === 1 && <Step2AddressHouse data={data} update={update} />}
          {step === 2 && <Step3Items data={data} update={update} />}
          {step === 3 && <StepRoomPhotos data={data} update={update} />}
          {step === 4 && <Step4DateTime data={data} update={update} />}
          {step === 5 && <Step1PersonalDetails data={data} update={update} />}
          {step === 6 && <Step5Summary data={data} />}

          <div className="mt-10 flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={back}
              disabled={step === 1 || submitting}
              data-testid="btn-back"
              className="h-12 px-5 rounded-full text-slate-700 hover:bg-slate-100 font-semibold disabled:opacity-40"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            {step < TOTAL_STEPS ? (
              <Button
                type="button"
                onClick={next}
                data-testid="btn-next"
                className="h-12 px-7 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white font-semibold shadow-md shadow-orange-200"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="btn-submit-booking"
                className="h-12 px-7 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white font-semibold shadow-md shadow-orange-200"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
                ) : (
                  <><Send className="h-4 w-4" /> Submit booking</>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function missingMessage(step, data) {
  if (step === 1) {
    if (!data.street_address.trim()) return "Street address is required";
    if (!isValidUKPostcode(data.postcode)) return "Enter a valid UK postcode";
    if (!data.property_type) return "Choose a property type";
  }
  if (step === 2) return "Add at least one item to collect";
  if (step === 3) return "At least one room photo is required";
  if (step === 4) return "Pick a preferred date";
  if (step === 5) {
    if (!data.full_name.trim()) return "Please add your full name";
    if (!isValidUKPhone(data.phone)) return "Enter a valid UK phone number";
    if (!EMAIL_RE.test(data.email)) return "Please enter a valid email";
  }
  return "Please complete this step";
}
